import type { PaymentStatus } from '@shared/api/graphql/generated';

import { formatMoney } from '@/shared/lib';
import {
  OrderFiscalFailureKind,
  PaymentApplicationStatus,
  PaymentAttentionReason,
  PaymentAttemptStatus,
  PaymentBlockScope,
  PaymentEventType,
  PaymentJournalKind,
  PaymentJournalMoneyStatus,
  PaymentLinkStatus,
  PaymentMoneyPartMethod,
  PaymentProvider,
  PaymentReceiptIssuer,
  PaymentReceiptOperation,
  PaymentReceiptStatus,
  ReceiptSettlementMethod,
} from '@shared/api/graphql/generated';
import { formatDateTime, inTimezone } from '@shared/lib/date-time';

import type { PaymentJournalRow } from '../model/types';

export const paymentProviderLabels: Record<PaymentProvider, string> = {
  [PaymentProvider.Kkmserver]: 'Касса локации',
  [PaymentProvider.Robokassa]: 'Robokassa',
  [PaymentProvider.Manual]: 'Без кассы',
};
export const paymentMoneyStatusLabels: Record<PaymentJournalMoneyStatus, string> = {
  [PaymentJournalMoneyStatus.Pending]: 'Ожидает оплаты',
  [PaymentJournalMoneyStatus.PartiallyCaptured]: 'Получена часть суммы',
  [PaymentJournalMoneyStatus.Captured]: 'Деньги получены',
  [PaymentJournalMoneyStatus.Failed]: 'Не выполнено',
  [PaymentJournalMoneyStatus.Unknown]: 'Нужна проверка денег',
  [PaymentJournalMoneyStatus.Reserved]: 'Возврат готовится',
  [PaymentJournalMoneyStatus.Sent]: 'Возврат отправлен',
  [PaymentJournalMoneyStatus.Accepted]: 'Возврат принят',
  [PaymentJournalMoneyStatus.Succeeded]: 'Деньги возвращены',
};
const PAYMENT_MONEY_STATUS_CLASSES: Record<PaymentJournalMoneyStatus, string> = {
  [PaymentJournalMoneyStatus.Pending]: 'bg-(--base-secondary-color) text-(--base-popup-secondary-color)',
  [PaymentJournalMoneyStatus.PartiallyCaptured]: 'bg-(--base-warning-color)/15 text-(--base-warning-color)',
  [PaymentJournalMoneyStatus.Captured]: 'bg-(--base-success-color)/15 text-(--base-success-color)',
  [PaymentJournalMoneyStatus.Failed]: 'bg-(--base-error-color)/15 text-(--base-error-color)',
  [PaymentJournalMoneyStatus.Unknown]: 'bg-(--base-warning-color)/15 text-(--base-warning-color)',
  [PaymentJournalMoneyStatus.Reserved]: 'bg-(--base-secondary-color) text-(--base-popup-secondary-color)',
  [PaymentJournalMoneyStatus.Sent]: 'bg-(--base-secondary-color) text-(--base-popup-secondary-color)',
  [PaymentJournalMoneyStatus.Accepted]: 'bg-(--base-secondary-color) text-(--base-popup-secondary-color)',
  [PaymentJournalMoneyStatus.Succeeded]: 'bg-(--base-confirm-color)/15 text-(--base-confirm-color)',
};
/** Карточка передаёт PaymentStatus: его значения совпадают с денежными статусами журнала. */
export const getPaymentMoneyStatusClass = (status: PaymentJournalMoneyStatus | PaymentStatus): string =>
  PAYMENT_MONEY_STATUS_CLASSES[status as PaymentJournalMoneyStatus];
export const paymentLinkStatusLabels: Record<PaymentLinkStatus, string> = {
  [PaymentLinkStatus.Issuing]: 'Ссылка создаётся',
  [PaymentLinkStatus.IssueUnknown]: 'Нужна проверка выпуска',
  [PaymentLinkStatus.IssueFailed]: 'Ссылка не создана',
  [PaymentLinkStatus.AwaitingPayment]: 'Ожидает оплаты',
  [PaymentLinkStatus.Deactivating]: 'Ссылка отзывается',
  [PaymentLinkStatus.Deactivated]: 'Ссылка отозвана',
  [PaymentLinkStatus.Expired]: 'Срок ссылки истёк',
  [PaymentLinkStatus.Paid]: 'Оплачена',
};
/** Подписи живут под заголовком «Чек», поэтому само слово в них не повторяется. */
export const paymentReceiptStatusLabels: Record<PaymentReceiptStatus, string> = {
  [PaymentReceiptStatus.Pending]: 'Ожидается',
  [PaymentReceiptStatus.Confirmed]: 'Пробит',
  [PaymentReceiptStatus.Failed]: 'Ошибка',
  [PaymentReceiptStatus.Unknown]: 'Нужна проверка',
  [PaymentReceiptStatus.Untracked]: 'Формирует Robokassa',
};
/** Вид чека: что он фиксирует. Чек при оплате по ссылке — предоплата, пока услуга не оказана. */
export const paymentReceiptKindLabel = (
  operation: PaymentReceiptOperation,
  settlementMethod: ReceiptSettlementMethod,
): string =>
  operation === PaymentReceiptOperation.Refund
    ? 'Возврат'
    : settlementMethod === ReceiptSettlementMethod.FullPrepayment
      ? 'Предоплата'
      : 'Полный расчёт';
export type PaymentReceiptTone = 'default' | 'warning' | 'error';
const RECEIPT_SUMMARY_ENDINGS: Record<PaymentReceiptStatus, { ending: string; tone: PaymentReceiptTone }> = {
  [PaymentReceiptStatus.Confirmed]: { ending: ' пробит', tone: 'default' },
  [PaymentReceiptStatus.Pending]: { ending: ' ожидается', tone: 'default' },
  [PaymentReceiptStatus.Untracked]: { ending: ', формирует Robokassa', tone: 'default' },
  [PaymentReceiptStatus.Unknown]: { ending: ' требует проверки', tone: 'warning' },
  [PaymentReceiptStatus.Failed]: { ending: ' не пробит', tone: 'error' },
};
/** Чек строки журнала одной фразой: «Чек № 1189 пробит», «Чек предоплаты, формирует Robokassa». */
export const paymentReceiptSummary = (
  row: Pick<
    PaymentJournalRow,
    'receipt_status' | 'receipt_operation' | 'receipt_settlement_method' | 'receipt_fd_number'
  >,
): { text: string; tone: PaymentReceiptTone } => {
  if (!row.receipt_status) return { text: 'Чек вне Mors', tone: 'default' };
  const kind =
    row.receipt_operation === PaymentReceiptOperation.Refund
      ? 'Чек возврата'
      : row.receipt_settlement_method === ReceiptSettlementMethod.FullPrepayment
        ? 'Чек предоплаты'
        : 'Чек';
  const { ending, tone } = RECEIPT_SUMMARY_ENDINGS[row.receipt_status];

  return { text: `${kind}${row.receipt_fd_number ? ` № ${row.receipt_fd_number}` : ''}${ending}`, tone };
};
/** Кем пробит чек. */
export const paymentReceiptIssuerLabels: Record<PaymentReceiptIssuer, string> = {
  [PaymentReceiptIssuer.LocationKkt]: 'Касса локации',
  [PaymentReceiptIssuer.RobokassaAgent]: 'Robokassa, агент',
  [PaymentReceiptIssuer.RobokassaOwn]: 'Robokassa, своя касса',
};
export const paymentApplicationLabels: Record<PaymentApplicationStatus, string> = {
  [PaymentApplicationStatus.Pending]: 'Аванс ожидает зачёта',
  [PaymentApplicationStatus.Applied]: 'Зачтён',
  [PaymentApplicationStatus.Failed]: 'Ошибка зачёта',
  [PaymentApplicationStatus.Rejected]: 'Аванс не зачтён',
};
export const paymentAttemptLabels: Record<PaymentAttemptStatus, string> = {
  [PaymentAttemptStatus.Prepared]: 'Подготовлена',
  [PaymentAttemptStatus.Sent]: 'Отправлена',
  [PaymentAttemptStatus.Succeeded]: 'Выполнена',
  [PaymentAttemptStatus.Failed]: 'Не выполнена',
  [PaymentAttemptStatus.Unknown]: 'Результат неизвестен',
  [PaymentAttemptStatus.Expired]: 'Срок истёк',
};
export const paymentMethodLabels: Record<PaymentMoneyPartMethod, string> = {
  [PaymentMoneyPartMethod.Cash]: 'Наличные',
  [PaymentMoneyPartMethod.Card]: 'Карта',
  [PaymentMoneyPartMethod.Online]: 'По ссылке',
  [PaymentMoneyPartMethod.Unknown]: 'Способ не указан',
};
/** `cardLabel` подменяет подпись безналичной части там, где известен инструмент, например «СБП». */
export const paymentMethodLabel = (method: PaymentMoneyPartMethod, cardLabel?: string): string =>
  method === PaymentMoneyPartMethod.Card && cardLabel ? cardLabel : paymentMethodLabels[method];
/** Способы оплаты строки журнала: «Карта», «Карта + наличные»; пустая строка, если частей платежа нет. */
export const paymentMethodsLabel = (methods: readonly PaymentMoneyPartMethod[], cardLabel?: string): string =>
  methods
    .map((method) => paymentMethodLabel(method, cardLabel))
    .map((label, index) => (index ? label.toLowerCase() : label))
    .join(' + ');
/** Части платежа с суммами: «Карта 1 000 ₽ + Наличные 500 ₽». */
export const paymentPartsLabel = (
  parts: readonly { method: PaymentMoneyPartMethod; amount: number }[],
  cardLabel?: string,
): string =>
  parts.map((part) => `${paymentMethodLabel(part.method, cardLabel)} ${formatMoney(part.amount)}`).join(' + ');
export const paymentAttentionLabels: Record<PaymentAttentionReason, string> = {
  [PaymentAttentionReason.MoneyUnknown]: 'Результат оплаты неизвестен',
  [PaymentAttentionReason.ReceiptFailed]: 'Не удалось пробить чек',
  [PaymentAttentionReason.ReceiptUnknown]: 'Неизвестно, пробит ли чек',
  [PaymentAttentionReason.ReceiptOverdue]: 'Чек не подтверждён более 30 минут',
  [PaymentAttentionReason.ApplicationFailed]: 'Деньги получены, оплата не проведена',
  [PaymentAttentionReason.AdvanceRejected]: 'Аванс не зачтён, доступен возврат',
  [PaymentAttentionReason.AdvanceRemainder]: 'Остаток аванса требует возврата',
  [PaymentAttentionReason.AdvanceOverdue]: 'Просроченный аванс не зачтён',
  [PaymentAttentionReason.LinkIssueUnknown]: 'Результат создания ссылки неизвестен',
  [PaymentAttentionReason.LinkDeactivationOverdue]: 'Отзыв ссылки не подтверждён',
  [PaymentAttentionReason.LinkIssueMismatch]: 'Параметры ссылки у провайдера не совпали',
  [PaymentAttentionReason.ProviderReversed]: 'Провайдер сообщил об отмене оплаты',
  [PaymentAttentionReason.ProviderSuspended]: 'Провайдер приостановил оплату',
  [PaymentAttentionReason.CallbackUnprocessed]: 'Уведомление об оплате не обработано',
  [PaymentAttentionReason.RefundUnknown]: 'Результат возврата неизвестен',
  [PaymentAttentionReason.RefundFailed]: 'Возврат не выполнен',
};
/** Причины внимания строки одной фразой. */
export const paymentAttentionSummary = (row: Pick<PaymentJournalRow, 'attention_reasons'>): string =>
  row.attention_reasons.map((reason) => paymentAttentionLabels[reason]).join(' · ');
/** Время строки журнала в зоне её локации. */
export const paymentJournalDateTime = (row: Pick<PaymentJournalRow, 'created_at' | 'timezone'>): string =>
  formatDateTime(inTimezone(row.created_at, row.timezone));
/** Сумма строки, у возврата со знаком минус: «−1 000 ₽». */
export const paymentJournalAmount = (row: Pick<PaymentJournalRow, 'kind' | 'amount'>): string =>
  `${row.kind === PaymentJournalKind.Refund ? '−' : ''}${formatMoney(row.amount)}`;
const refundStatusLabels: Partial<Record<PaymentJournalMoneyStatus, string>> = {
  [PaymentJournalMoneyStatus.Failed]: paymentAttentionLabels[PaymentAttentionReason.RefundFailed],
  [PaymentJournalMoneyStatus.Unknown]: paymentAttentionLabels[PaymentAttentionReason.RefundUnknown],
};
/** Статус строки журнала: отказ и неизвестный результат возврата названы теми же словами, что причина внимания. */
export const paymentJournalStatusLabel = (kind: PaymentJournalKind, status: PaymentJournalMoneyStatus): string =>
  (kind === PaymentJournalKind.Refund ? refundStatusLabels[status] : undefined) ?? paymentMoneyStatusLabels[status];
const JOURNAL_SHORT_STATUS_LABELS: Record<PaymentJournalMoneyStatus, string> = {
  [PaymentJournalMoneyStatus.Pending]: 'Ожидает',
  [PaymentJournalMoneyStatus.PartiallyCaptured]: 'Частично',
  [PaymentJournalMoneyStatus.Captured]: 'Получено',
  [PaymentJournalMoneyStatus.Failed]: 'Не прошло',
  [PaymentJournalMoneyStatus.Unknown]: 'Проверить',
  [PaymentJournalMoneyStatus.Reserved]: 'Готовится',
  [PaymentJournalMoneyStatus.Sent]: 'Отправлен',
  [PaymentJournalMoneyStatus.Accepted]: 'Принят',
  [PaymentJournalMoneyStatus.Succeeded]: 'Возвращено',
};
/** Плашка под суммой журнала: направление денег уже видно по знаку суммы. */
export const paymentJournalShortStatusLabel = (kind: PaymentJournalKind, status: PaymentJournalMoneyStatus): string =>
  kind === PaymentJournalKind.Refund && status === PaymentJournalMoneyStatus.Failed
    ? 'Не возвращено'
    : JOURNAL_SHORT_STATUS_LABELS[status];
/** Неудачная попытка возврата, после которой прошёл другой возврат: сервер уже снял с неё внимание. */
export const isSupersededRefundAttempt = (
  row: Pick<PaymentJournalRow, 'kind' | 'money_status' | 'attention_reasons'>,
): boolean =>
  row.kind === PaymentJournalKind.Refund &&
  row.money_status === PaymentJournalMoneyStatus.Failed &&
  !row.attention_reasons.includes(PaymentAttentionReason.RefundFailed);
const MONEY_MOVED_STATUSES = new Set<PaymentJournalMoneyStatus | null | undefined>([
  PaymentJournalMoneyStatus.Captured,
  PaymentJournalMoneyStatus.PartiallyCaptured,
  PaymentJournalMoneyStatus.Accepted,
  PaymentJournalMoneyStatus.Succeeded,
]);
/** Деньги строки двинулись: платёж получен или возврат принят банком. */
export const paymentJournalMoneyMoved = (status: PaymentJournalMoneyStatus | null | undefined): boolean =>
  MONEY_MOVED_STATUSES.has(status);
const FISCAL_FAILURE_KIND_LABELS: Record<OrderFiscalFailureKind, string> = {
  [OrderFiscalFailureKind.KnownPrePrint]: 'Касса отказала до печати',
  [OrderFiscalFailureKind.PaymentDeclined]: 'Отказ банка',
  [OrderFiscalFailureKind.PaymentCancelled]: 'Отменено на терминале',
  [OrderFiscalFailureKind.PaymentReversed]: 'Деньги возвращены клиенту',
  [OrderFiscalFailureKind.TransportUnknown]: 'Нет связи с кассой',
  [OrderFiscalFailureKind.MalformedResponse]: 'Непонятный ответ кассы',
  [OrderFiscalFailureKind.MappingUnsupported]: 'Касса не поддерживает операцию',
  [OrderFiscalFailureKind.ManagerConfirmedNotRun]: 'Подтверждено, что операции не было',
  [OrderFiscalFailureKind.OwnerForceClosed]: 'Закрыта аварийно',
  [OrderFiscalFailureKind.CancelledNotCharged]: 'Списания не было',
  [OrderFiscalFailureKind.CancelledUnverified]: 'Отменена без проверки',
  [OrderFiscalFailureKind.ShiftClosedUnconfirmed]: 'Закрыта со сменой без подтверждения',
};
/** Причина отказа строки одной фразой: текст кассы, затем стадия отказа кассы, затем код провайдера. */
export const paymentJournalFailureText = (
  row: Pick<PaymentJournalRow, 'failure_message' | 'failure_kind' | 'failure_reason_code'>,
): string | null =>
  row.failure_message ??
  (row.failure_kind ? FISCAL_FAILURE_KIND_LABELS[row.failure_kind] : null) ??
  (row.failure_reason_code ? paymentReasonLabel(row.failure_reason_code) : null);
export const paymentBlockLabels: Record<PaymentBlockScope, string> = {
  [PaymentBlockScope.None]: 'Другие операции доступны',
  [PaymentBlockScope.Order]: 'Оплата заблокирована',
};
export const paymentEventTypeLabels: Record<PaymentEventType, string> = {
  [PaymentEventType.Created]: 'Платёж создан',
  [PaymentEventType.OperatorAction]: 'Действие сотрудника',
  [PaymentEventType.ProviderEvidence]: 'Ответ провайдера',
  [PaymentEventType.StatusChanged]: 'Состояние обновлено',
};

export function paymentEventStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    created: 'Создано',
    prepared: 'Подготовлено',
    sent: 'Отправлено',
    succeeded: 'Выполнено',
    captured: 'Деньги получены',
    partially_captured: 'Часть денег получена',
    failed: 'Не выполнено',
    unknown: 'Нужна проверка',
    pending: 'Ожидает выполнения',
    applied: 'Зачтено',
    rejected: 'Не зачтено',
    confirmed: 'Подтверждено',
    issuing: 'Ссылка создаётся',
    issue_unknown: 'Выпуск ссылки не подтверждён',
    issue_failed: 'Ссылка не создана',
    awaiting_payment: 'Ожидает оплаты',
    paid: 'Оплачено',
    deactivating: 'Отзывается',
    deactivated: 'Отозвано',
    expired: 'Срок истёк',
    reserved: 'Зарезервировано',
    accepted: 'Принято провайдером',
    untracked: 'Проверяется у провайдера',
  };
  return labels[status] ?? 'Состояние обновлено';
}
export function paymentReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    refund_credentials_missing: 'Для возврата нужен пароль № 3 в настройках Robokassa.',
    refund_test_mode_unsupported: 'В тестовом режиме Robokassa возвраты недоступны.',
    refund_rejected: 'Robokassa отклонила возврат.',
    receipt_provider_failed: 'Robokassa не пробила чек.',
    op_key_missing:
      'Провайдер не предоставил идентификатор оплаты для возврата. Проверьте операцию в кабинете Robokassa.',
    provider_state_verified: 'Состояние проверено у провайдера.',
    provider_reversed: 'Провайдер сообщил об отмене оплаты.',
    provider_suspended: 'Оплата приостановлена провайдером.',
    amount_mismatch: 'Сумма провайдера не совпала с ожидаемой.',
    basis_closed: 'Основание уже закрыто, аванс не зачтён.',
    checks_exhausted: 'Автоматическая проверка не подтвердила результат.',
    receipt_status_timeout: 'Провайдер не подтвердил чек вовремя.',
    link_issue_mismatch: 'Параметры ссылки у провайдера не совпали.',
  };
  return labels[reason] ?? 'Результат операции требует проверки.';
}
